import React, { Suspense, lazy } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import { CssBaseline, Box, CircularProgress, LinearProgress } from '@mui/material';
import { AuthProvider } from './context/AuthContext';
import Layout from './components/Layout/Layout';
import Login from './components/Auth/Login';
import PrivateRoute from './components/Auth/PrivateRoute';
import SuperAdminRoute from './components/Auth/SuperAdminRoute';
import AppColors from './utils/AppColors';

// Lazy load components for code splitting and better performance
const Dashboard = lazy(() => import('./components/Dashboard/Dashboard'));
const UserManagement = lazy(() => import('./components/Users/UserManagement'));
const UserDetailedView = lazy(() => import('./components/Users/UserDetailedView'));
const AdminManagement = lazy(() => import('./components/Admin/AdminManagement'));
const VendorManagement = lazy(() => import('./components/Vendors/VendorManagement'));
const VendorDetailedView = lazy(() => import('./components/Vendors/VendorDetailedView'));
const VendorEditPage = lazy(() => import('./components/Vendors/VendorEditPage'));
const VendorCreatePage = lazy(() => import('./components/Vendors/VendorCreatePage'));
const VoucherSupplierManagement = lazy(() => import('./components/VoucherSuppliers/VoucherSupplierManagement'));
const VoucherManagement = lazy(() => import('./components/Vouchers/VoucherManagement'));
const TransactionManagement = lazy(() => import('./components/Transactions/TransactionManagement'));
const RequestManagement = lazy(() => import('./components/Requests/RequestManagement'));
const RequestDetailedView = lazy(() => import('./components/Requests/RequestDetailedView'));
const AdminSupport = lazy(() => import('./components/AdminSupportRedesigned'));
const UserContextPage = lazy(() => import('./components/UserContextPage'));
const Analytics = lazy(() => import('./components/Analytics/Analytics'));
const AnalyticsDownloads = lazy(() => import('./pages/AnalyticsDownloads'));
const SystemManagement = lazy(() => import('./components/System/SystemManagement'));
const Settings = lazy(() => import('./components/Settings/Settings'));
const AppVersionManagement = lazy(() => import('./components/Settings/AppVersionManagement'));
const EmiratesManagement = lazy(() => import('./components/Settings/EmiratesManagement'));
const CatalogManagement = lazy(() => import('./components/Settings/CatalogManagement'));
const Announcements = lazy(() => import('./components/Communications/Announcements'));
const AccountingReports = lazy(() => import('./components/Accounting/AccountingReportsRevolutionized'));

// V2 Marketplace Components
const ListingManagement = lazy(() => import('./components/Listings/ListingManagement'));
const ListingApprovalQueue = lazy(() => import('./components/Listings/ListingApprovalQueue'));
const ListingDetailedView = lazy(() => import('./components/Listings/ListingDetailedView'));
const C2COfferManagement = lazy(() => import('./components/C2COffers/C2COfferManagement'));
const TangoPlusManagement = lazy(() => import('./components/TangoPlus/TangoPlusManagement'));
const ListingReportsManagement = lazy(() => import('./components/ListingReports/ListingReportsManagement'));
const PromoCodeManagement = lazy(() => import('./components/Marketing/PromoCodeManagement'));

// Modern loading component
const PageLoader = () => (
  <Box sx={{ 
    display: 'flex', 
    flexDirection: 'column',
    alignItems: 'center', 
    justifyContent: 'center', 
    minHeight: '100vh',
    background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)'
  }}>
    <CircularProgress size={60} thickness={4} sx={{ color: AppColors.primaryDark, mb: 2 }} />
    <LinearProgress sx={{ width: '200px', borderRadius: '10px' }} />
  </Box>
);

// Create theme - aligned with the mobile app's modern design language
const theme = createTheme({
  palette: {
    primary: { main: AppColors.primaryDark },
    secondary: { main: AppColors.primaryYellow },
    success: { main: AppColors.successGreen },
    warning: { main: AppColors.warningOrange },
    error: { main: AppColors.errorRed },
    info: { main: AppColors.infoBlue },
    background: {
      default: '#F5F7FA',
      paper: AppColors.backgroundWhite,
    },
    text: {
      primary: AppColors.textPrimary,
      secondary: AppColors.textSecondary,
    },
  },
  shape: { borderRadius: 14 },
  typography: {
    fontFamily: 'Beiruti, "Inter", "Segoe UI", Arial, sans-serif',
    h1: { fontWeight: 700, letterSpacing: '-1px' },
    h2: { fontWeight: 700, letterSpacing: '-0.5px' },
    h3: { fontWeight: 700 },
    h4: { fontWeight: 700 },
    h5: { fontWeight: 700 },
    h6: { fontWeight: 600 },
    button: { textTransform: 'none', fontWeight: 600 },
  },
  components: {
    MuiCssBaseline: {
      styleOverrides: {
        body: {
          backgroundColor: '#F5F7FA',
        },
      },
    },
    MuiButton: {
      defaultProps: { disableElevation: true },
      styleOverrides: {
        root: { borderRadius: 12, textTransform: 'none', fontWeight: 600, paddingInline: 18 },
        containedPrimary: {
          boxShadow: '0 6px 16px rgba(14,37,55,0.18)',
          '&:hover': { boxShadow: '0 8px 22px rgba(14,37,55,0.24)' },
        },
      },
    },
    MuiPaper: {
      styleOverrides: {
        root: { borderRadius: 16, backgroundImage: 'none' },
      },
    },
    MuiCard: {
      styleOverrides: {
        root: {
          borderRadius: 18,
          border: '1px solid rgba(14,37,55,0.06)',
          boxShadow: '0 4px 14px rgba(14,37,55,0.05)',
        },
      },
    },
    MuiAppBar: {
      styleOverrides: { root: { boxShadow: 'none' } },
    },
    MuiTextField: {
      defaultProps: { size: 'small' },
    },
    MuiOutlinedInput: {
      styleOverrides: {
        root: { borderRadius: 12 },
      },
    },
    MuiChip: {
      styleOverrides: { root: { fontWeight: 600 } },
    },
    MuiTableCell: {
      styleOverrides: {
        head: {
          fontWeight: 700,
          color: AppColors.textSecondary,
          textTransform: 'uppercase',
          fontSize: '0.72rem',
          letterSpacing: '0.5px',
          background: '#F5F7FA',
          borderBottom: '1px solid rgba(14,37,55,0.08)',
        },
        body: { fontSize: '0.875rem' },
      },
    },
    MuiTableContainer: {
      styleOverrides: {
        root: { borderRadius: 16, border: '1px solid rgba(14,37,55,0.06)' },
      },
    },
    MuiDialog: {
      styleOverrides: { paper: { borderRadius: 20 } },
    },
    MuiTooltip: {
      styleOverrides: {
        tooltip: {
          background: AppColors.primaryDark,
          fontSize: '0.72rem',
          padding: '6px 10px',
          borderRadius: 8,
        },
        arrow: { color: AppColors.primaryDark },
      },
    },
  },
});

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <Router
          future={{
            v7_startTransition: true,
            v7_relativeSplatPath: true
          }}
        >
          <Suspense fallback={<PageLoader />}>
            <Routes>
              <Route path="/login" element={<Login />} />
            
            {/* Protected routes */}
            <Route path="/dashboard" element={
              <PrivateRoute>
                <Layout>
                  <Dashboard />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/users" element={
              <PrivateRoute>
                <Layout>
                  <UserManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/users/:userId" element={
              <PrivateRoute>
                <Layout>
                  <UserDetailedView />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/users/:userId/activity" element={
              <PrivateRoute>
                <Layout>
                  <UserDetailedView />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors" element={
              <PrivateRoute>
                <Layout>
                  <VendorManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors/create" element={
              <PrivateRoute>
                <Layout>
                  <VendorCreatePage />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors/new" element={
              <PrivateRoute>
                <Layout>
                  <VendorCreatePage />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors/:id" element={
              <PrivateRoute>
                <Layout>
                  <VendorDetailedView />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors/:id/edit" element={
              <PrivateRoute>
                <Layout>
                  <VendorEditPage />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vendors/:vendorId/activity" element={
              <PrivateRoute>
                <Layout>
                  <VendorDetailedView />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/voucher-suppliers" element={
              <PrivateRoute>
                <Layout>
                  <VoucherSupplierManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/vouchers" element={
              <PrivateRoute>
                <Layout>
                  <VoucherManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/transactions" element={
              <PrivateRoute>
                <Layout>
                  <TransactionManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/requests" element={
              <PrivateRoute>
                <Layout>
                  <RequestManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/requests/:id" element={
              <PrivateRoute>
                <Layout>
                  <RequestDetailedView />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/support" element={
              <PrivateRoute>
                <Layout>
                  <AdminSupport />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/support/context/:userId/:userType" element={
              <PrivateRoute>
                <Layout>
                  <UserContextPage />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/analytics" element={
              <PrivateRoute>
                <Layout>
                  <Analytics />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/analytics/downloads" element={
              <PrivateRoute>
                <Layout>
                  <AnalyticsDownloads />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/accounting-reports" element={
              <PrivateRoute>
                <Layout>
                  <AccountingReports />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/system" element={
              <PrivateRoute>
                <Layout>
                  <SystemManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/admin-management" element={
              <SuperAdminRoute>
                <Layout>
                  <AdminManagement />
                </Layout>
              </SuperAdminRoute>
            } />
            
            <Route path="/settings" element={
              <PrivateRoute>
                <Layout>
                  <Settings />
                </Layout>
              </PrivateRoute>
            } />
            
            <Route path="/app-versions" element={
              <PrivateRoute>
                <Layout>
                  <AppVersionManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/emirates" element={
              <PrivateRoute>
                <Layout>
                  <EmiratesManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/catalog" element={
              <PrivateRoute>
                <Layout>
                  <CatalogManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/announcements" element={
              <PrivateRoute>
                <Layout>
                  <Announcements />
                </Layout>
              </PrivateRoute>
            } />

            {/* V2 Marketplace Routes */}
            <Route path="/listings" element={
              <PrivateRoute>
                <Layout>
                  <ListingManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/listings/approvals" element={
              <PrivateRoute>
                <Layout>
                  <ListingApprovalQueue />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/listings/:id" element={
              <PrivateRoute>
                <Layout>
                  <ListingDetailedView />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/c2c-offers" element={
              <PrivateRoute>
                <Layout>
                  <C2COfferManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/tango-quotes" element={
              <PrivateRoute>
                <Layout>
                  <TangoPlusManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/listing-reports" element={
              <PrivateRoute>
                <Layout>
                  <ListingReportsManagement />
                </Layout>
              </PrivateRoute>
            } />

            <Route path="/promo-codes" element={
              <PrivateRoute>
                <Layout>
                  <PromoCodeManagement />
                </Layout>
              </PrivateRoute>
            } />
            
            {/* Default redirect */}
              <Route path="/" element={<Navigate to="/dashboard" replace />} />
              <Route path="*" element={<Navigate to="/dashboard" />} />
            </Routes>
          </Suspense>
        </Router>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
